import { Banner, Button, Field, Input, LoadingState } from '@nolens/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { NolensMark } from '../app/Brand';
import { keys, useMe } from '../lib/queries';
import { signOut } from '../lib/session';
import { auth } from '../lib/supabase';

type State =
  | { kind: 'loading' }
  | { kind: 'enroll'; factorId: string; qr: string; secret: string }
  | { kind: 'verify'; factorId: string }
  | { kind: 'error'; message: string };

/**
 * Segundo fator da equipa (TOTP, Supabase Auth). Na primeira vez regista a app autenticadora
 * (QR code ou chave); depois só pede o código de 6 dígitos. Com aal2 a API e o RLS
 * devolvem os papéis de equipa (migração staff_mfa).
 */
export function MfaPage() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!auth || me.data?.mfaRequired !== true) return;
    let cancelled = false;
    void (async () => {
      const { data, error: listError } = await auth.mfa.listFactors();
      if (listError) return !cancelled && setState({ kind: 'error', message: 'Não foi possível preparar a verificação. Recarregue a página.' });
      const verified = data.totp[0];
      if (verified) return !cancelled && setState({ kind: 'verify', factorId: verified.id });
      // Registo interrompido antes: começa de novo (um fator por verificar não serve para nada).
      for (const f of data.all.filter((f) => f.factor_type === 'totp' && f.status === 'unverified')) await auth.mfa.unenroll({ factorId: f.id });
      const { data: enrolled, error: enrollError } = await auth.mfa.enroll({ factorType: 'totp', friendlyName: 'Nolens Physio', issuer: 'Nolens Physio' });
      if (cancelled) return;
      if (enrollError || !enrolled) return setState({ kind: 'error', message: 'Não foi possível iniciar o registo. Recarregue a página.' });
      setState({ kind: 'enroll', factorId: enrolled.id, qr: enrolled.totp.qr_code, secret: enrolled.totp.secret });
    })();
    return () => {
      cancelled = true;
    };
  }, [me.data?.mfaRequired]);

  if (me.isPending) return <LoadingState />;
  if (me.data && !me.data.mfaRequired) return <Navigate to="/" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!auth || (state.kind !== 'enroll' && state.kind !== 'verify')) return;
    setBusy(true);
    setError(undefined);
    const { error: verifyError } = await auth.mfa.challengeAndVerify({ factorId: state.factorId, code: code.trim() });
    setBusy(false);
    if (verifyError) {
      setCode('');
      return setError('O código não está certo ou já mudou. Use o código que aparece agora na app.');
    }
    // A sessão passa a aal2: tudo o que foi pedido antes (sem papéis de equipa) recarrega.
    await qc.invalidateQueries();
    await qc.refetchQueries({ queryKey: keys.me });
    void navigate('/', { replace: true });
  };

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-5 py-10">
      <NolensMark />
      {state.kind === 'loading' && <LoadingState />}
      {state.kind === 'error' && <Banner tone="critical" className="mt-8">{state.message}</Banner>}
      {(state.kind === 'enroll' || state.kind === 'verify') && (
        <form onSubmit={(e) => void submit(e)} className="mt-8 flex flex-col gap-5">
          {state.kind === 'enroll' ? (
            <>
              <div>
                <h1 className="text-[26px] font-semibold tracking-tight">Proteja a sua conta</h1>
                <p className="mt-2 text-text-2">
                  A área clínica tem dados de saúde. Para entrar, além do email, usa um código de uma app autenticadora no telemóvel
                  (Google Authenticator, Microsoft Authenticator, 1Password…).
                </p>
              </div>
              <ol className="flex list-decimal flex-col gap-2 pl-5 text-[15px]">
                <li>Abra a app autenticadora e adicione uma conta nova.</li>
                <li>Leia este código QR com a câmara da app.</li>
                <li>Escreva abaixo o código de 6 dígitos que aparece.</li>
              </ol>
              <img src={state.qr} alt="Código QR para a app autenticadora" width={192} height={192} className="mx-auto rounded-card bg-white p-3" />
              <details className="text-sm text-text-2">
                <summary className="cursor-pointer">Não consegue ler o QR? Use a chave</summary>
                <p className="mt-2 break-all rounded-input bg-surface-2 px-3 py-2 font-mono text-text-1">{state.secret}</p>
              </details>
            </>
          ) : (
            <div>
              <h1 className="text-[26px] font-semibold tracking-tight">Código da app autenticadora</h1>
              <p className="mt-2 text-text-2">Abra a app autenticadora e escreva o código de 6 dígitos da Nolens Physio.</p>
            </div>
          )}
          <Field label="Código de 6 dígitos" error={error}>
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
                className="text-center text-xl tracking-[0.4em]"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            )}
          </Field>
          <Button type="submit" size="lg" fullWidth loading={busy} disabled={code.length !== 6}>
            {state.kind === 'enroll' ? 'Ativar e entrar' : 'Entrar'}
          </Button>
          {state.kind === 'verify' && (
            <p className="text-sm text-text-2">Perdeu o telemóvel? Peça à equipa Nolens para repor a verificação da sua conta.</p>
          )}
          <button type="button" className="text-sm text-text-2 hover:underline" onClick={() => void signOut()}>
            Terminar sessão
          </button>
        </form>
      )}
    </main>
  );
}
